import { useRef, useState } from 'react';
import { Typography } from '@mui/material';
import ConfirmAction from 'components/popups/ConfirmAction';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';

// Retain the prepared payload so confirming a retry cannot convert dates or taxes twice.
export default function useSeasonEventSave() {
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState(null);
    const inFlight = useRef(false);
    const { notify } = useNotification();

    async function save({ endpoint, method, body, onSuccess, fallback }) {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setPending(null);
        try {
            const response = await fetchExpresso(endpoint, { method, body });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                await onSuccess(data);
            } else if (
                response.status === 409 &&
                data.code === 'season_access_change_confirmation_required'
            ) {
                setPending({ endpoint, method, body, onSuccess, fallback, data });
            } else {
                notify.error(data.message || fallback);
            }
        } catch {
            notify.error(fallback);
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }

    const confirmationDialog = (
        <ConfirmAction
            open={Boolean(pending)}
            title="Confirm Season Event"
            confirmText="Confirm"
            onCancel={() => setPending(null)}
            onConfirm={() => {
                if (!pending) return;
                save({
                    ...pending,
                    body: { ...pending.body, confirmSeasonAccessChange: true },
                });
            }}
            description={
                <Typography paragraph>
                    {pending?.body.parentEventId === null
                        ? 'Season ticket holders will no longer have access to this event when it is removed from the season.'
                        : 'Season ticket holders will be able to access this event when its added to the season.'}
                </Typography>
            }
        />
    );

    return { save, loading, confirmationDialog };
}
